import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FormElement } from './types';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
export type ImportStatus = 'idle' | 'importing' | 'error';
export type PlacementTool = 'checkbox' | 'radio' | null;

export interface ActiveTableCell {
  elementId: string;
  row: number;
  col: number;
}

interface FormDesignerUiState {
  currentPageId: string | null;
  selectedIds: string[];
  zoom: number;
  clipboard: FormElement[] | null;
  saveStatus: SaveStatus;
  publishStatus: SaveStatus;
  clientId: string | null;
  clientName: string | null;
  templateCategory: string;
  activeCell: ActiveTableCell | null;
  importStatus: ImportStatus;
  activeTool: PlacementTool;
}

const initialState: FormDesignerUiState = {
  currentPageId: null,
  selectedIds: [],
  zoom: 1.15,
  clipboard: null,
  saveStatus: 'idle',
  publishStatus: 'idle',
  clientId: null,
  clientName: null,
  templateCategory: '',
  activeCell: null,
  importStatus: 'idle',
  activeTool: null,
};

const formDesignerUiSlice = createSlice({
  name: 'formDesignerUi',
  initialState,
  reducers: {
    setCurrentPageId: (state, action: PayloadAction<string | null>) => {
      state.currentPageId = action.payload;
    },
    setSelection: (state, action: PayloadAction<string[]>) => {
      state.selectedIds = action.payload;
      state.activeCell = null;
    },
    setActiveCell: (state, action: PayloadAction<ActiveTableCell | null>) => {
      state.activeCell = action.payload;
    },
    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = Math.min(2, Math.max(0.25, action.payload));
    },
    setClipboard: (state, action: PayloadAction<FormElement[] | null>) => {
      state.clipboard = action.payload;
    },
    setSaveStatus: (state, action: PayloadAction<SaveStatus>) => {
      state.saveStatus = action.payload;
    },
    setPublishStatus: (state, action: PayloadAction<SaveStatus>) => {
      state.publishStatus = action.payload;
    },
    setClient: (state, action: PayloadAction<{ clientId: string | null; clientName: string | null }>) => {
      state.clientId = action.payload.clientId;
      state.clientName = action.payload.clientName;
    },
    setTemplateCategory: (state, action: PayloadAction<string>) => {
      state.templateCategory = action.payload;
    },
    setImportStatus: (state, action: PayloadAction<ImportStatus>) => {
      state.importStatus = action.payload;
    },
    setActiveTool: (state, action: PayloadAction<PlacementTool>) => {
      state.activeTool = action.payload;
    },
    resetUi: () => initialState,
  },
});

export const {
  setCurrentPageId,
  setSelection,
  setActiveCell,
  setZoom,
  setClipboard,
  setSaveStatus,
  setPublishStatus,
  setClient,
  setTemplateCategory,
  setImportStatus,
  setActiveTool,
  resetUi,
} = formDesignerUiSlice.actions;

export default formDesignerUiSlice.reducer;
